import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Heading } from '@/components/Cms/Heading/Heading';
import { NextImage } from '@/components/NextImage';
import type {
  ContentfulCollectionTeaserFragment,
  ContentfulHeadingFragment,
  ContentfulImageWithFocalPointFragment,
} from '@/graphql/contentful/api';
import { useIsMdDown } from '@/hooks/useMedia';
import { useRouter } from '@/hooks/useRouter';
import { algoliaSearchClient } from '@/integrations/algolia/client';
import { getIndexName } from '@/integrations/algolia/helpers';
import type { WatchHit } from '@/integrations/algolia/types';
import { messages } from '@/messages/collectionTeaser';
import { useCommonModalProviderContext } from '@/providers/CommonModalProvider';

import { separateThousandsWithPrime } from './helpers';

import { ReactComponent as InfoSVG } from '@/assets/icons/info_icon.svg';

import * as S from './CollectionTeaser.styles';

export const CollectionTeaser = (props: ContentfulCollectionTeaserFragment) => {
  const {
    backgroundMedia,
    collectionName,
    collectionTeaserHeading,
    collectionTeaserText,
    productsPickerCollection,
    url,
    urlText,
  } = props;
  const [lowestPrice, setLowestPrice] = useState<number>();
  const [headingProps, setHeadingProps] = useState<ContentfulHeadingFragment>();
  const [currency, setCurrency] = useState<string>();
  const isMdDown = useIsMdDown();
  const { formatMessage } = useIntl();
  const { asPath, locale, push } = useRouter();
  const index = algoliaSearchClient.initIndex(getIndexName('PRODUCT', locale));
  const { setPriceModalStatus } = useCommonModalProviderContext();

  useEffect(() => {
    if (collectionTeaserHeading) {
      setHeadingProps({
        ...collectionTeaserHeading,
        textAlignment: isMdDown ? 'center' : 'left',
      });
    }
  }, [isMdDown]);
  useEffect(() => {
    const fetchCollectionData = async () => {
      try {
        if (collectionName) {
          const { hits } = await index.search('', {
            facetFilters: `collection:${collectionName}`,
            attributesToRetrieve: ['price', 'currency'],
            hitsPerPage: 2000,
          });

          const sortedHits = (hits as WatchHit[]).sort(
            (a, b) => a.price - b.price,
          );
          setLowestPrice(sortedHits[0]?.price);
          setCurrency(sortedHits[0]?.currency);
        }
      } catch (e) {
        console.log(e);
      }
    };

    fetchCollectionData();
  }, [collectionName]);
  const tripticImageSize = {
    height: isMdDown ? 240 : 320,
    width: isMdDown ? 180 : 198,
  };
  const redirectToCollection = () => {
    push({
      pathname: url ?? asPath,
    });
  };
  const showPriceModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPriceModalStatus(true);
  };
  return (
    <S.TeaserWrapper data-test-id='CollectionTeaserWrapper'>
      <S.Wrapper>
        <S.StyledRow data-test-id='CollectionTeaserRow'>
          <S.Description
            data-test-id='CollectionTeaserDescription'
            md={6}
            xs={12}
          >
            <S.HeadingWrapper
              data-test-id='CollectionTeaserHeadingWrapper'
              onClick={redirectToCollection}
            >
              <Heading Heading={headingProps as ContentfulHeadingFragment} />
            </S.HeadingWrapper>
            <S.Text
              dangerouslySetInnerHTML={{
                __html: documentToHtmlString(collectionTeaserText?.json),
              }}
              data-test-id='CollectionTeaserText'
            />
            <S.StyledPrice data-test-id='CollectionTeaserPrice'>
              <S.SpanCapitalize>
                {formatMessage(messages.from)}
              </S.SpanCapitalize>
              {' ' + separateThousandsWithPrime(lowestPrice) + ' '}
              {currency && currency + ' '}
              <InfoSVG
                data-test-id='CollectionTeaserIcon'
                height={14}
                onClick={showPriceModal}
                width={14}
              />
            </S.StyledPrice>
            <S.Cta
              data-test-id='CollectionTeaserCta'
              isFluid={false}
              label={urlText ?? 'Discover the collection'}
              onClick={redirectToCollection}
              variant='outlined'
            />
          </S.Description>
          <S.ProductsImages
            data-test-id='CollectionTeaserProductsImages'
            md={6}
            xs={12}
          >
            <S.ImagesWrapper data-test-id='CollectionTeaserImagesWrapper'>
              <S.TripticImages
                data-test-id='CollectionTeaserTripticImages'
                onClick={redirectToCollection}
              >
                <S.LeftAndRightImageContainer data-test-id='CollectionTeaserLeftRightImage-0'>
                  <NextImage
                    alt='triptic image'
                    height={tripticImageSize.height}
                    sizes='100vw'
                    src={
                      ((
                        productsPickerCollection?.items[0]
                          ?.image as ContentfulImageWithFocalPointFragment
                      )?.image?.url as string) ?? ''
                    }
                    width={tripticImageSize.width}
                    {...{
                      priority: true,
                      loading: 'eager',
                      'data-test-id': 'CollectionTeaserImage-0',
                    }}
                  />
                </S.LeftAndRightImageContainer>
                <S.MidImageContainer data-test-id='CollectionTeaserMidImageContainer'>
                  <NextImage
                    alt='triptic image'
                    height={tripticImageSize.height}
                    sizes='100vw'
                    src={
                      ((
                        productsPickerCollection?.items[1]
                          ?.image as ContentfulImageWithFocalPointFragment
                      )?.image?.url as string) ?? ''
                    }
                    width={tripticImageSize.width}
                    {...{
                      priority: true,
                      loading: 'eager',
                      'data-test-id': 'CollectionTeaserImage-1',
                    }}
                  />
                </S.MidImageContainer>
                <S.LeftAndRightImageContainer data-test-id='CollectionTeaserLeftRightImage-2'>
                  <NextImage
                    alt='triptic image'
                    height={tripticImageSize.height}
                    sizes='100vw'
                    src={
                      ((
                        productsPickerCollection?.items[2]
                          ?.image as ContentfulImageWithFocalPointFragment
                      )?.image?.url as string) ?? ''
                    }
                    width={tripticImageSize.width}
                    {...{
                      priority: true,
                      loading: 'eager',
                      'data-test-id': 'CollectionTeaserImage-2',
                    }}
                  />
                </S.LeftAndRightImageContainer>
              </S.TripticImages>
            </S.ImagesWrapper>
          </S.ProductsImages>
        </S.StyledRow>
      </S.Wrapper>
      <S.MediaWrapper
        data-test-id='CollectionTeaserMediaWrapper'
        mediaAnimationEffect={null}
      >
        <S.MediaImage
          alt={backgroundMedia?.title ?? 'Teaser Media'}
          height={0}
          sizes='100vw'
          src={(backgroundMedia?.url as string) ?? ''}
          width={0}
          {...{
            priority: true,
            loading: 'eager',
            'data-test-id': 'CollectionTeaserMediaImage',
          }}
        />
      </S.MediaWrapper>
      <S.MobileCta
        data-test-id='CollectionTeaserMobileCta'
        isFluid={true}
        label={urlText ?? 'Discover the collection'}
        onClick={redirectToCollection}
        variant='outlined'
      />
    </S.TeaserWrapper>
  );
};
